import classNames from 'classnames';
import { FormattedValue } from 'components/shared/ReadOnlyGrid/FormattedValue';
import type { ReadOnlyGridColumn } from 'components/shared/ReadOnlyGrid/ReadOnlyGrid';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import type { IntlKey } from 'locales';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import type { AssetClass } from 'state/blotters/priceImprovement';
import type { GenericTable } from 'state/quotes/details/details.model';

interface Props {
  data: Omit<GenericTable, 'name'>;
  precision: number;
  displayHeader?: boolean;
  displayMultiColumn?: boolean;
  tableClassName?: string;
  assetClass?: AssetClass;
  e2e: string;
}
export const BasicGenericGrid: FC<Props> = ({
  data,
  precision,
  displayHeader,
  tableClassName,
  displayMultiColumn,
  e2e,
}) => {
  return (
    <table className={classNames('table table-sm my-0,', tableClassName)} data-e2e={e2e}>
      {displayHeader && (
        <thead>
          <tr
            className={classNames({
              ' justify-content-md-between ': displayMultiColumn,
            })}
          >
            {data.columns.map(column => (
              <th key={column.name}>
                <ColumnHeader column={column} />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {data.cells.map((line, i) => (
          <tr
            key={`${line[data.columns[0].name]}-${i}`}
            className={classNames('border-bottom ', {
              'justify-content-md-between ': displayMultiColumn,
            })}
          >
            {data.columns.map((column, index) => {
              const value = line[column.name] as number;
              if (index === 0 && isNaN(value)) {
                return (
                  <td key={`${column.name}-${index}`} className="text-secondary text-start align-middle border-top-0">
                    {value}
                  </td>
                );
              }
              const textAlignement = data.columns.length > 2 ? 'text-start' : 'text-end';
              return (
                <td
                  key={`${column.name}-${index}`}
                  className={classNames('align-middle border-top-0 ', textAlignement)}
                >
                  {value !== undefined && <CellValue column={column} value={value} precision={precision} />}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const ColumnHeader: FC<{ column: ReadOnlyGridColumn }> = ({ column }) => {
  const { formatMessage } = useIntl();
  const label =
    column.alias ?? formatMessage({ id: `table.header.column.${column.name}` as IntlKey, defaultMessage: column.name });
  return <>{label}</>;
};

export const CellValue: FC<{ column: ReadOnlyGridColumn; value: number; precision: number }> = ({
  column,
  value,
  precision,
}) => {
  const unit = column?.unit;

  return (
    <SpTooltip contents={value} position="left">
      <div>
        <FormattedValue value={value} precision={precision} />
        {unit !== undefined && <span className="ps-1">{unit}</span>}
      </div>
    </SpTooltip>
  );
};
